import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import api from '../../utils/axiosConfig';
import { showToast } from '../../features/ui/uiSlice';
import { isStaff } from '../../features/auth/authSlice';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
    UserIcon,
    EnvelopeIcon,
    PhoneIcon,
    MapPinIcon,
    CalendarIcon,
    PencilIcon,
    CheckIcon,
    XMarkIcon,
    AcademicCapIcon,
    BriefcaseIcon,
    BuildingOfficeIcon,
    IdentificationIcon,
    ShieldCheckIcon,
    StarIcon,
    GlobeAltIcon,
    HeartIcon,
    SparklesIcon,
    TrophyIcon,
    ChartBarIcon,
    ClockIcon,
    PlusIcon
} from '@heroicons/react/24/outline';
import MultiSkillSelect from '../../components/UI/MultiSkillSelect';

interface UserProfile {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    location?: string;
    bio?: string;
    experience?: string;
    education?: string;
    skillsDescription?: string;
    skills?: Array<{
        _id: string;
        name: string;
    }>;
    qualification?: string;
    designation?: string;
    region?: string;
    personalDetails?: {
        dateOfBirth?: string;
        address?: {
            street?: string;
            city?: string;
            state?: string;
            pincode?: string;
            country?: string;
        };
        qualifications?: Array<{
            degree?: string;
            institution?: string;
            year?: number;
            percentage?: number;
        }>;
        panNumber?: string;
        aadhaarNumber?: string;
    };
    bankDetails?: {
        accountNumber?: string;
        bankName?: string;
        ifscCode?: string;
        branchName?: string;
        accountHolderName?: string;
    };
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
}

interface Skill {
    _id: string;
    name: string;
    description?: string;
}

const profileSchema = yup.object({
    firstName: yup.string().required('First name is required'),
    lastName: yup.string().required('Last name is required'),
    phone: yup.string().optional(),
    location: yup.string().optional(),
    bio: yup.string().optional(),
    experience: yup.string().optional(),
    skills: yup.array().optional(),
    qualification: yup.string().optional(),
    designation: yup.string().optional(),
    region: yup.string().optional(),
    personalDetails: yup.object({
        dateOfBirth: yup.string().optional(),
        address: yup.object({
            street: yup.string().optional(),
            city: yup.string().optional(),
            state: yup.string().optional(),
            district: yup.string().optional(),
            pincode: yup.string().optional(),
            country: yup.string().optional(),
        }).optional(),
        panNumber: yup.string().matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, 'Invalid PAN format').optional(),
        aadhaarNumber: yup.string().matches(/^[0-9]{12}$/, 'Invalid Aadhaar format').optional(),
    }).optional(),
    bankDetails: yup.object({
        accountNumber: yup.string().optional(),
        bankName: yup.string().optional(),
        ifscCode: yup.string().optional(),
        branchName: yup.string().optional(),
        accountHolderName: yup.string().optional(),
    }).optional(),
});

const Profile: React.FC = () => {
    const dispatch = useAppDispatch();
    const { user } = useAppSelector((state) => state.auth);
    const [profile, setProfile] = useState<UserProfile | null>(null);
    const [skills, setSkills] = useState<Skill[]>([]);
    const [isEditing, setIsEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);

    // Helper function to calculate profile completeness
    const calculateProfileCompleteness = (profile: UserProfile | null): number => {
        if (!profile) return 0;

        // Define fields based on user role
        let fields: string[];
        if (user && isStaff(user)) {
            // For staff users, only include basic fields
            fields = ['firstName', 'lastName', 'email', 'phone', 'location'];
        } else {
            // For regular users, include all fields
            fields = ['firstName', 'lastName', 'email', 'phone', 'location', 'bio', 'experience', 'skills', 'qualification', 'designation', 'region'];
        }

        const filledFields = fields.filter(field => {
            if (field === 'skills') return profile.skills && profile.skills.length > 0;
            const value = profile[field as keyof UserProfile];
            return value && (value as string) !== '';
        });

        return Math.round((filledFields.length / fields.length) * 100);
    };

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        watch,
        setValue,
    } = useForm({
        resolver: yupResolver(profileSchema),
        defaultValues: {
            firstName: '',
            lastName: '',
            phone: '',
            location: '',
            bio: '',
            experience: '',
            skills: [],
            qualification: '',
            designation: '',
            region: '',
            personalDetails: {
                dateOfBirth: '',
                address: {
                    street: '',
                    city: '',
                    state: '',
                    pincode: '',
                    country: '',
                },
                panNumber: '',
                aadhaarNumber: '',
            },
            bankDetails: {
                accountNumber: '',
                bankName: '',
                ifscCode: '',
                branchName: '',
                accountHolderName: '',
            },
        },
    });

    const fetchProfile = async () => {
        try {
            setLoading(true);
            const response = await api.get('/auth/me');
            const userData = response.data.data;
            setProfile(userData);

            // Reset form with user data
            reset({
                firstName: userData?.firstName || '',
                lastName: userData?.lastName || '',
                phone: userData?.phone || '',
                location: userData?.location || '',
                bio: userData?.bio || '',
                experience: userData?.experience || '',
                skills: userData?.skills?.map((skill: any) => skill._id) || [],
                qualification: userData?.qualification || '',
                designation: userData?.designation || '',
                region: userData?.region || '',
                personalDetails: userData?.personalDetails || {},
                bankDetails: userData?.bankDetails || {},
            });
        } catch (error: any) {
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to fetch profile',
                type: 'error'
            }));
        } finally {
            setLoading(false);
        }
    };

    const fetchSkills = async () => {
        try {
            const response = await api.get('/skills/all');
            setSkills(response.data.data);
        } catch (error: any) {
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to fetch skills',
                type: 'error'
            }));
        }
    };

    useEffect(() => {
        fetchProfile();
        fetchSkills();
    }, []);

    const onSubmit = async (data: any) => {
        try {
            setSaving(true);

            // Clean up data - remove empty strings and undefined values
            const optionalFields = ['phone', 'location', 'bio', 'experience', 'qualification', 'designation', 'region'];
            const cleanedData = { ...data };

            optionalFields.forEach(field => {
                if (cleanedData[field] === '') {
                    delete cleanedData[field];
                }
            });

            // Clean personalDetails
            if (cleanedData.personalDetails) {
                Object.keys(cleanedData.personalDetails).forEach(key => {
                    if (cleanedData.personalDetails[key] === '') {
                        delete cleanedData.personalDetails[key];
                    }
                });

                if (cleanedData.personalDetails.address) {
                    Object.keys(cleanedData.personalDetails.address).forEach(key => {
                        if (cleanedData.personalDetails.address[key] === '') {
                            delete cleanedData.personalDetails.address[key];
                        }
                    });
                }
            }

            // Clean bankDetails
            if (cleanedData.bankDetails) {
                Object.keys(cleanedData.bankDetails).forEach(key => {
                    if (cleanedData.bankDetails[key] === '') {
                        delete cleanedData.bankDetails[key];
                    }
                });
            }

            const response = await api.put('/auth/profile', cleanedData);
            setProfile(response.data.data);
            setIsEditing(false);

            dispatch(showToast({
                message: 'Profile updated successfully',
                type: 'success'
            }));
        } catch (error: any) {
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to update profile',
                type: 'error'
            }));
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setIsEditing(false);
        fetchProfile(); // Reset form with original data
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-400">Loading profile...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header Section */}
                <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 rounded-2xl shadow-2xl mb-8">
                    <div className="absolute inset-0 bg-black/10"></div>
                    <div className="relative px-8 py-12">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                            <div className="flex items-center space-x-6">
                                {/* Profile Avatar */}
                                <div className="relative">
                                    <div className="w-24 h-24 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border-4 border-white/30">
                                        <UserIcon className="w-12 h-12 text-white" />
                                    </div>
                                    <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-green-500 rounded-full border-4 border-white flex items-center justify-center">
                                        <ShieldCheckIcon className="w-4 h-4 text-white" />
                                    </div>
                                </div>

                                {/* Profile Info */}
                                <div className="text-white">
                                    <h1 className="text-3xl font-bold mb-2">
                                        {profile?.firstName} {profile?.lastName}
                                    </h1>
                                    <p className="text-blue-100 text-lg mb-2">{profile?.email}</p>
                                    <div className="flex items-center space-x-4 mb-4">
                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-white/20 backdrop-blur-sm">
                                            <StarIcon className="w-4 h-4 mr-1" />
                                            {profile?.designation?.replace('_', ' ').toUpperCase() || 'MEMBER'}
                                        </span>
                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-white/20 backdrop-blur-sm">
                                            <GlobeAltIcon className="w-4 h-4 mr-1" />
                                            {profile?.region?.toUpperCase() || 'REGION'}
                                        </span>
                                    </div>

                                    {/* Profile Completeness Progress */}
                                    <div className="w-full">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm font-medium text-blue-100">Profile Completeness</span>
                                            <span className="text-sm font-bold text-white">
                                                {calculateProfileCompleteness(profile)}%
                                            </span>
                                        </div>
                                        <div className="w-full bg-white/20 rounded-full h-2">
                                            <div
                                                className="bg-gradient-to-r from-green-400 to-blue-500 h-2 rounded-full transition-all duration-500"
                                                style={{
                                                    width: `${calculateProfileCompleteness(profile)}%`
                                                }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Action Button */}
                            {!isEditing && (
                                <div className="mt-6 lg:mt-0">
                                    <button
                                        onClick={() => setIsEditing(true)}
                                        className="inline-flex items-center px-6 py-3 bg-white/20 backdrop-blur-sm text-white text-sm font-semibold rounded-xl hover:bg-white/30 focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-blue-600 transition-all duration-200 border border-white/30"
                                    >
                                        <PencilIcon className="h-5 w-5 mr-2" />
                                        Edit Profile
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Profile Form */}
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
                    {/* Personal Information */}
                    <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 dark:border-gray-700/50 p-8">
                        <div className="flex items-center mb-8">
                            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center mr-4">
                                <UserIcon className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                    Personal Information
                                </h2>
                                <p className="text-gray-600 dark:text-gray-400">
                                    Your basic profile details
                                </p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            {/* First Name */}
                            <div className="space-y-2">
                                <label className="flex items-center text-sm font-semibold text-gray-700 dark:text-gray-300">
                                    <UserIcon className="w-4 h-4 mr-2 text-blue-500" />
                                    First Name *
                                </label>
                                <input
                                    {...register('firstName')}
                                    disabled={!isEditing}
                                    className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-200 focus:outline-none focus:ring-0 ${errors.firstName
                                        ? 'border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/20'
                                        : 'border-gray-200 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400'
                                        } ${!isEditing ? 'bg-gray-50 dark:bg-gray-700 cursor-not-allowed' : 'bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-500'} text-gray-900 dark:text-white`}
                                    placeholder="Enter your first name"
                                />
                                {errors.firstName && (
                                    <p className="text-sm text-red-600 dark:text-red-400 flex items-center">
                                        <span className="w-1 h-1 bg-red-500 rounded-full mr-2"></span>
                                        {errors.firstName.message}
                                    </p>
                                )}
                            </div>

                            {/* Last Name */}
                            <div className="space-y-2">
                                <label className="flex items-center text-sm font-semibold text-gray-700 dark:text-gray-300">
                                    <UserIcon className="w-4 h-4 mr-2 text-blue-500" />
                                    Last Name *
                                </label>
                                <input
                                    {...register('lastName')}
                                    disabled={!isEditing}
                                    className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-200 focus:outline-none focus:ring-0 ${errors.lastName
                                        ? 'border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/20'
                                        : 'border-gray-200 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400'
                                        } ${!isEditing ? 'bg-gray-50 dark:bg-gray-700 cursor-not-allowed' : 'bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-500'} text-gray-900 dark:text-white`}
                                    placeholder="Enter your last name"
                                />
                                {errors.lastName && (
                                    <p className="text-sm text-red-600 dark:text-red-400 flex items-center">
                                        <span className="w-1 h-1 bg-red-500 rounded-full mr-2"></span>
                                        {errors.lastName.message}
                                    </p>
                                )}
                            </div>

                            {/* Email */}
                            <div className="space-y-2">
                                <label className="flex items-center text-sm font-semibold text-gray-700 dark:text-gray-300">
                                    <EnvelopeIcon className="w-4 h-4 mr-2 text-blue-500" />
                                    Email Address
                                </label>
                                <div className="relative">
                                    <EnvelopeIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                    <input
                                        type="email"
                                        value={profile?.email || ''}
                                        disabled
                                        className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                                    />
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                                    <ShieldCheckIcon className="w-3 h-3 mr-1" />
                                    Email cannot be changed for security reasons
                                </p>
                            </div>

                            {/* Phone */}
                            <div className="space-y-2">
                                <label className="flex items-center text-sm font-semibold text-gray-700 dark:text-gray-300">
                                    <PhoneIcon className="w-4 h-4 mr-2 text-blue-500" />
                                    Phone Number
                                </label>
                                <div className="relative">
                                    <PhoneIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                    <input
                                        {...register('phone')}
                                        disabled={!isEditing}
                                        className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl transition-all duration-200 focus:outline-none focus:ring-0 ${errors.phone
                                            ? 'border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/20'
                                            : 'border-gray-200 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400'
                                            } ${!isEditing ? 'bg-gray-50 dark:bg-gray-700 cursor-not-allowed' : 'bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-500'} text-gray-900 dark:text-white`}
                                        placeholder="Enter your phone number"
                                    />
                                </div>
                                {errors.phone && (
                                    <p className="text-sm text-red-600 dark:text-red-400 flex items-center">
                                        <span className="w-1 h-1 bg-red-500 rounded-full mr-2"></span>
                                        {errors.phone.message}
                                    </p>
                                )}
                            </div>

                            {/* Location */}
                            <div className="space-y-2">
                                <label className="flex items-center text-sm font-semibold text-gray-700 dark:text-gray-300">
                                    <MapPinIcon className="w-4 h-4 mr-2 text-blue-500" />
                                    Location
                                </label>
                                <div className="relative">
                                    <MapPinIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                    <input
                                        {...register('location')}
                                        disabled={!isEditing}
                                        className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl transition-all duration-200 focus:outline-none focus:ring-0 ${errors.location
                                            ? 'border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/20'
                                            : 'border-gray-200 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400'
                                            } ${!isEditing ? 'bg-gray-50 dark:bg-gray-700 cursor-not-allowed' : 'bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-500'} text-gray-900 dark:text-white`}
                                        placeholder="Enter your location"
                                    />
                                </div>
                                {errors.location && (
                                    <p className="text-sm text-red-600 dark:text-red-400 flex items-center">
                                        <span className="w-1 h-1 bg-red-500 rounded-full mr-2"></span>
                                        {errors.location.message}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Professional Information - Hidden for staff */}
                    {user && !isStaff(user) && (
                        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 dark:border-gray-700/50 p-8">
                            <div className="flex items-center mb-8">
                                <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-teal-500 rounded-xl flex items-center justify-center mr-4">
                                    <BriefcaseIcon className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                        Professional Information
                                    </h2>
                                    <p className="text-gray-600 dark:text-gray-400">
                                        Your skills, experience, and qualifications
                                    </p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                {/* Skills */}
                                <div className="space-y-2">
                                    <label className="flex items-center text-sm font-semibold text-gray-700 dark:text-gray-300">
                                        <SparklesIcon className="w-4 h-4 mr-2 text-green-500" />
                                        Skills & Expertise
                                    </label>

                                    {/* Skills Display */}
                                    {!isEditing ? (
                                        <div className="min-h-[48px] px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700">
                                            {profile?.skills && profile.skills.length > 0 ? (
                                                <div className="flex flex-wrap gap-2">
                                                    {profile.skills.map((skill: any) => (
                                                        <span key={skill._id} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                                            <SparklesIcon className="w-3 h-3 mr-1" />
                                                            {skill.name}
                                                        </span>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className="text-gray-500 dark:text-gray-400 text-sm">No skills selected</p>
                                            )}
                                        </div>
                                    ) : (
                                        <MultiSkillSelect
                                            selectedSkills={watch('skills') || []}
                                            onSkillsChange={(skills) => setValue('skills', skills)}
                                            placeholder="Select your skills..."
                                            showSearch={true}
                                            className={errors.skills ? 'border-red-300 dark:border-red-600' : ''}
                                            maxSkills={8}
                                        />
                                    )}

                                    {errors.skills && (
                                        <p className="text-sm text-red-600 dark:text-red-400 flex items-center">
                                            <span className="w-1 h-1 bg-red-500 rounded-full mr-2"></span>
                                            {errors.skills.message}
                                        </p>
                                    )}
                                </div>

                                {/* Bio */}
                                <div className="space-y-2">
                                    <label className="flex items-center text-sm font-semibold text-gray-700 dark:text-gray-300">
                                        <HeartIcon className="w-4 h-4 mr-2 text-green-500" />
                                        Bio
                                    </label>
                                    <textarea
                                        {...register('bio')}
                                        disabled={!isEditing}
                                        rows={4}
                                        className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-200 focus:outline-none focus:ring-0 ${errors.bio
                                            ? 'border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/20'
                                            : 'border-gray-200 dark:border-gray-600 focus:border-green-500 dark:focus:border-green-400'
                                            } ${!isEditing ? 'bg-gray-50 dark:bg-gray-700 cursor-not-allowed' : 'bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-500'} text-gray-900 dark:text-white resize-none`}
                                        placeholder="Tell us about yourself..."
                                    />
                                    {errors.bio && (
                                        <p className="text-sm text-red-600 dark:text-red-400 flex items-center">
                                            <span className="w-1 h-1 bg-red-500 rounded-full mr-2"></span>
                                            {errors.bio.message}
                                        </p>
                                    )}
                                </div>



                                {/* Qualification */}
                                <div className="space-y-2">
                                    <label className="flex items-center text-sm font-semibold text-gray-700 dark:text-gray-300">
                                        <AcademicCapIcon className="w-4 h-4 mr-2 text-green-500" />
                                        Qualification
                                    </label>
                                    <div className="relative">
                                        <AcademicCapIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                        <select
                                            {...register('qualification')}
                                            disabled={!isEditing}
                                            className={`w-full pl-12 pr-10 py-3 border-2 rounded-xl transition-all duration-200 focus:outline-none focus:ring-0 appearance-none ${errors.qualification
                                                ? 'border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/20'
                                                : 'border-gray-200 dark:border-gray-600 focus:border-green-500 dark:focus:border-green-400'
                                                } ${!isEditing ? 'bg-gray-50 dark:bg-gray-700 cursor-not-allowed' : 'bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-500'} text-gray-900 dark:text-white`}
                                        >
                                            <option value="">Select qualification</option>
                                            <option value="high-school">High School</option>
                                            <option value="diploma">Diploma</option>
                                            <option value="bachelor">Bachelor's Degree</option>
                                            <option value="master">Master's Degree</option>
                                            <option value="phd">PhD</option>
                                            <option value="other">Other</option>
                                        </select>
                                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </div>
                                    </div>
                                    {errors.qualification && (
                                        <p className="text-sm text-red-600 dark:text-red-400 flex items-center">
                                            <span className="w-1 h-1 bg-red-500 rounded-full mr-2"></span>
                                            {errors.qualification.message}
                                        </p>
                                    )}
                                </div>

                                {/* Designation */}
                                <div className="space-y-2">
                                    <label className="flex items-center text-sm font-semibold text-gray-700 dark:text-gray-300">
                                        <BriefcaseIcon className="w-4 h-4 mr-2 text-green-500" />
                                        Designation
                                    </label>
                                    <div className="relative">
                                        <BriefcaseIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                        <select
                                            {...register('designation')}
                                            disabled={!isEditing}
                                            className={`w-full pl-12 pr-10 py-3 border-2 rounded-xl transition-all duration-200 focus:outline-none focus:ring-0 appearance-none ${errors.designation
                                                ? 'border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/20'
                                                : 'border-gray-200 dark:border-gray-600 focus:border-green-500 dark:focus:border-green-400'
                                                } ${!isEditing ? 'bg-gray-50 dark:bg-gray-700 cursor-not-allowed' : 'bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-500'} text-gray-900 dark:text-white`}
                                        >
                                            <option value="">Select designation</option>
                                            <option value="team_executive">Team Executive</option>
                                            <option value="team_leader">Team Leader</option>
                                            <option value="district_head">District Head</option>
                                            <option value="state_head">State Head</option>
                                            <option value="regional_head">Regional Head</option>
                                        </select>
                                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </div>
                                    </div>
                                    {errors.designation && (
                                        <p className="text-sm text-red-600 dark:text-red-400 flex items-center">
                                            <span className="w-1 h-1 bg-red-500 rounded-full mr-2"></span>
                                            {errors.designation.message}
                                        </p>
                                    )}
                                </div>

                                {/* Experience Level */}
                                <div className="space-y-2">
                                    <label className="flex items-center text-sm font-semibold text-gray-700 dark:text-gray-300">
                                        <TrophyIcon className="w-4 h-4 mr-2 text-green-500" />
                                        Experience Level
                                    </label>
                                    <div className="relative">
                                        <TrophyIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                        <select
                                            {...register('experience')}
                                            disabled={!isEditing}
                                            className={`w-full pl-12 pr-10 py-3 border-2 rounded-xl transition-all duration-200 focus:outline-none focus:ring-0 appearance-none ${errors.experience
                                                ? 'border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/20'
                                                : 'border-gray-200 dark:border-gray-600 focus:border-green-500 dark:focus:border-green-400'
                                                } ${!isEditing ? 'bg-gray-50 dark:bg-gray-700 cursor-not-allowed' : 'bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-500'} text-gray-900 dark:text-white`}
                                        >
                                            <option value="">Select experience level</option>
                                            <option value="fresher">Fresher (0-1 years)</option>
                                            <option value="junior">Junior (1-3 years)</option>
                                            <option value="mid-level">Mid-level (3-5 years)</option>
                                            <option value="senior">Senior (5-8 years)</option>
                                            <option value="expert">Expert (8+ years)</option>
                                        </select>
                                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </div>
                                    </div>
                                    {errors.experience && (
                                        <p className="text-sm text-red-600 dark:text-red-400 flex items-center">
                                            <span className="w-1 h-1 bg-red-500 rounded-full mr-2"></span>
                                            {errors.experience.message}
                                        </p>
                                    )}
                                </div>

                                {/* Region */}
                                <div className="space-y-2">
                                    <label className="flex items-center text-sm font-semibold text-gray-700 dark:text-gray-300">
                                        <GlobeAltIcon className="w-4 h-4 mr-2 text-green-500" />
                                        Region
                                    </label>
                                    <div className="relative">
                                        <GlobeAltIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                        <select
                                            {...register('region')}
                                            disabled={!isEditing}
                                            className={`w-full pl-12 pr-10 py-3 border-2 rounded-xl transition-all duration-200 focus:outline-none focus:ring-0 appearance-none ${errors.region
                                                ? 'border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/20'
                                                : 'border-gray-200 dark:border-gray-600 focus:border-green-500 dark:focus:border-green-400'
                                                } ${!isEditing ? 'bg-gray-50 dark:bg-gray-700 cursor-not-allowed' : 'bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-500'} text-gray-900 dark:text-white`}
                                        >
                                            <option value="">Select region</option>
                                            <option value="north">North</option>
                                            <option value="south">South</option>
                                            <option value="east">East</option>
                                            <option value="west">West</option>
                                            <option value="central">Central</option>
                                        </select>
                                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </div>
                                    </div>
                                    {errors.region && (
                                        <p className="text-sm text-red-600 dark:text-red-400 flex items-center">
                                            <span className="w-1 h-1 bg-red-500 rounded-full mr-2"></span>
                                            {errors.region.message}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Personal Details - Hidden for staff */}
                    {user && !isStaff(user) && (
                        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 dark:border-gray-700/50 p-8">
                            <div className="flex items-center mb-8">
                                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mr-4">
                                    <IdentificationIcon className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                        Personal Details
                                    </h2>
                                    <p className="text-gray-600 dark:text-gray-400">
                                        Your personal information and identification
                                    </p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                {/* Date of Birth */}
                                <div className="space-y-2">
                                    <label className="flex items-center text-sm font-semibold text-gray-700 dark:text-gray-300">
                                        <CalendarIcon className="w-4 h-4 mr-2 text-purple-500" />
                                        Date of Birth
                                    </label>
                                    <div className="relative">
                                        <CalendarIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                        <input
                                            type="date"
                                            {...register('personalDetails.dateOfBirth')}
                                            disabled={!isEditing}
                                            className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl transition-all duration-200 focus:outline-none focus:ring-0 ${errors.personalDetails?.dateOfBirth
                                                ? 'border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/20'
                                                : 'border-gray-200 dark:border-gray-600 focus:border-purple-500 dark:focus:border-purple-400'
                                                } ${!isEditing ? 'bg-gray-50 dark:bg-gray-700 cursor-not-allowed' : 'bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-500'} text-gray-900 dark:text-white`}
                                        />
                                    </div>
                                    {errors.personalDetails?.dateOfBirth && (
                                        <p className="text-sm text-red-600 dark:text-red-400 flex items-center">
                                            <span className="w-1 h-1 bg-red-500 rounded-full mr-2"></span>
                                            {errors.personalDetails.dateOfBirth.message}
                                        </p>
                                    )}
                                </div>

                                {/* PAN Number */}
                                <div className="space-y-2">
                                    <label className="flex items-center text-sm font-semibold text-gray-700 dark:text-gray-300">
                                        <IdentificationIcon className="w-4 h-4 mr-2 text-purple-500" />
                                        PAN Number
                                    </label>
                                    <div className="relative">
                                        <IdentificationIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                        <input
                                            {...register('personalDetails.panNumber')}
                                            disabled={!isEditing}
                                            className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl transition-all duration-200 focus:outline-none focus:ring-0 ${errors.personalDetails?.panNumber
                                                ? 'border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/20'
                                                : 'border-gray-200 dark:border-gray-600 focus:border-purple-500 dark:focus:border-purple-400'
                                                } ${!isEditing ? 'bg-gray-50 dark:bg-gray-700 cursor-not-allowed' : 'bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-500'} text-gray-900 dark:text-white`}
                                            placeholder="ABCDE1234F"
                                            maxLength={10}
                                        />
                                    </div>
                                    {errors.personalDetails?.panNumber && (
                                        <p className="text-sm text-red-600 dark:text-red-400 flex items-center">
                                            <span className="w-1 h-1 bg-red-500 rounded-full mr-2"></span>
                                            {errors.personalDetails.panNumber.message}
                                        </p>
                                    )}
                                </div>

                                {/* Aadhaar Number */}
                                <div className="space-y-2">
                                    <label className="flex items-center text-sm font-semibold text-gray-700 dark:text-gray-300">
                                        <IdentificationIcon className="w-4 h-4 mr-2 text-purple-500" />
                                        Aadhaar Number
                                    </label>
                                    <div className="relative">
                                        <IdentificationIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                        <input
                                            {...register('personalDetails.aadhaarNumber')}
                                            disabled={!isEditing}
                                            className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl transition-all duration-200 focus:outline-none focus:ring-0 ${errors.personalDetails?.aadhaarNumber
                                                ? 'border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/20'
                                                : 'border-gray-200 dark:border-gray-600 focus:border-purple-500 dark:focus:border-purple-400'
                                                } ${!isEditing ? 'bg-gray-50 dark:bg-gray-700 cursor-not-allowed' : 'bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-500'} text-gray-900 dark:text-white`}
                                            placeholder="123456789012"
                                            maxLength={12}
                                        />
                                    </div>
                                    {errors.personalDetails?.aadhaarNumber && (
                                        <p className="text-sm text-red-600 dark:text-red-400 flex items-center">
                                            <span className="w-1 h-1 bg-red-500 rounded-full mr-2"></span>
                                            {errors.personalDetails.aadhaarNumber.message}
                                        </p>
                                    )}
                                </div>

                                {/* Address Street */}
                                <div className="space-y-2">
                                    <label className="flex items-center text-sm font-semibold text-gray-700 dark:text-gray-300">
                                        <MapPinIcon className="w-4 h-4 mr-2 text-purple-500" />
                                        Street Address
                                    </label>
                                    <div className="relative">
                                        <MapPinIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                        <input
                                            {...register('personalDetails.address.street')}
                                            disabled={!isEditing}
                                            className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl transition-all duration-200 focus:outline-none focus:ring-0 ${errors.personalDetails?.address?.street
                                                ? 'border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/20'
                                                : 'border-gray-200 dark:border-gray-600 focus:border-purple-500 dark:focus:border-purple-400'
                                                } ${!isEditing ? 'bg-gray-50 dark:bg-gray-700 cursor-not-allowed' : 'bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-500'} text-gray-900 dark:text-white`}
                                            placeholder="Enter street address"
                                        />
                                    </div>
                                    {errors.personalDetails?.address?.street && (
                                        <p className="text-sm text-red-600 dark:text-red-400 flex items-center">
                                            <span className="w-1 h-1 bg-red-500 rounded-full mr-2"></span>
                                            {errors.personalDetails.address.street.message}
                                        </p>
                                    )}
                                </div>

                                {/* Address City */}
                                <div className="space-y-2">
                                    <label className="flex items-center text-sm font-semibold text-gray-700 dark:text-gray-300">
                                        <BuildingOfficeIcon className="w-4 h-4 mr-2 text-purple-500" />
                                        City
                                    </label>
                                    <div className="relative">
                                        <BuildingOfficeIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                        <input
                                            {...register('personalDetails.address.city')}
                                            disabled={!isEditing}
                                            className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl transition-all duration-200 focus:outline-none focus:ring-0 ${errors.personalDetails?.address?.city
                                                ? 'border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/20'
                                                : 'border-gray-200 dark:border-gray-600 focus:border-purple-500 dark:focus:border-purple-400'
                                                } ${!isEditing ? 'bg-gray-50 dark:bg-gray-700 cursor-not-allowed' : 'bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-500'} text-gray-900 dark:text-white`}
                                            placeholder="Enter city"
                                        />
                                    </div>
                                    {errors.personalDetails?.address?.city && (
                                        <p className="text-sm text-red-600 dark:text-red-400 flex items-center">
                                            <span className="w-1 h-1 bg-red-500 rounded-full mr-2"></span>
                                            {errors.personalDetails.address.city.message}
                                        </p>
                                    )}
                                </div>

                                {/* Address State */}
                                <div className="space-y-2">
                                    <label className="flex items-center text-sm font-semibold text-gray-700 dark:text-gray-300">
                                        <MapPinIcon className="w-4 h-4 mr-2 text-purple-500" />
                                        State
                                    </label>
                                    <div className="relative">
                                        <MapPinIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                        <select
                                            {...register('personalDetails.address.state')}
                                            disabled={!isEditing}
                                            className={`w-full pl-12 pr-10 py-3 border-2 rounded-xl transition-all duration-200 focus:outline-none focus:ring-0 appearance-none ${errors.personalDetails?.address?.state
                                                ? 'border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/20'
                                                : 'border-gray-200 dark:border-gray-600 focus:border-purple-500 dark:focus:border-purple-400'
                                                } ${!isEditing ? 'bg-gray-50 dark:bg-gray-700 cursor-not-allowed' : 'bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-500'} text-gray-900 dark:text-white`}
                                        >
                                            <option value="">Select state</option>
                                            <option value="Andhra Pradesh">Andhra Pradesh</option>
                                            <option value="Arunachal Pradesh">Arunachal Pradesh</option>
                                            <option value="Assam">Assam</option>
                                            <option value="Bihar">Bihar</option>
                                            <option value="Chhattisgarh">Chhattisgarh</option>
                                            <option value="Goa">Goa</option>
                                            <option value="Gujarat">Gujarat</option>
                                            <option value="Haryana">Haryana</option>
                                            <option value="Himachal Pradesh">Himachal Pradesh</option>
                                            <option value="Jharkhand">Jharkhand</option>
                                            <option value="Karnataka">Karnataka</option>
                                            <option value="Kerala">Kerala</option>
                                            <option value="Madhya Pradesh">Madhya Pradesh</option>
                                            <option value="Maharashtra">Maharashtra</option>
                                            <option value="Manipur">Manipur</option>
                                            <option value="Meghalaya">Meghalaya</option>
                                            <option value="Mizoram">Mizoram</option>
                                            <option value="Nagaland">Nagaland</option>
                                            <option value="Odisha">Odisha</option>
                                            <option value="Punjab">Punjab</option>
                                            <option value="Rajasthan">Rajasthan</option>
                                            <option value="Sikkim">Sikkim</option>
                                            <option value="Tamil Nadu">Tamil Nadu</option>
                                            <option value="Telangana">Telangana</option>
                                            <option value="Tripura">Tripura</option>
                                            <option value="Uttar Pradesh">Uttar Pradesh</option>
                                            <option value="Uttarakhand">Uttarakhand</option>
                                            <option value="West Bengal">West Bengal</option>
                                            <option value="Andaman and Nicobar Islands">Andaman and Nicobar Islands</option>
                                            <option value="Chandigarh">Chandigarh</option>
                                            <option value="Dadra and Nagar Haveli and Daman and Diu">Dadra and Nagar Haveli and Daman and Diu</option>
                                            <option value="Delhi">Delhi</option>
                                            <option value="Jammu and Kashmir">Jammu and Kashmir</option>
                                            <option value="Ladakh">Ladakh</option>
                                            <option value="Lakshadweep">Lakshadweep</option>
                                            <option value="Puducherry">Puducherry</option>
                                        </select>
                                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </div>
                                    </div>
                                    {errors.personalDetails?.address?.state && (
                                        <p className="text-sm text-red-600 dark:text-red-400 flex items-center">
                                            <span className="w-1 h-1 bg-red-500 rounded-full mr-2"></span>
                                            {errors.personalDetails.address.state.message}
                                        </p>
                                    )}
                                </div>

                                {/* Address District */}
                                <div className="space-y-2">
                                    <label className="flex items-center text-sm font-semibold text-gray-700 dark:text-gray-300">
                                        <BuildingOfficeIcon className="w-4 h-4 mr-2 text-purple-500" />
                                        District
                                    </label>
                                    <div className="relative">
                                        <BuildingOfficeIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                        <input
                                            {...register('personalDetails.address.district')}
                                            disabled={!isEditing}
                                            className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl transition-all duration-200 focus:outline-none focus:ring-0 ${errors.personalDetails?.address?.district
                                                ? 'border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/20'
                                                : 'border-gray-200 dark:border-gray-600 focus:border-purple-500 dark:focus:border-purple-400'
                                                } ${!isEditing ? 'bg-gray-50 dark:bg-gray-700 cursor-not-allowed' : 'bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-500'} text-gray-900 dark:text-white`}
                                            placeholder="Enter district"
                                        />
                                    </div>
                                    {errors.personalDetails?.address?.district && (
                                        <p className="text-sm text-red-600 dark:text-red-400 flex items-center">
                                            <span className="w-1 h-1 bg-red-500 rounded-full mr-2"></span>
                                            {errors.personalDetails.address.district.message}
                                        </p>
                                    )}
                                </div>

                                {/* Address Pincode */}
                                <div className="space-y-2">
                                    <label className="flex items-center text-sm font-semibold text-gray-700 dark:text-gray-300">
                                        <MapPinIcon className="w-4 h-4 mr-2 text-purple-500" />
                                        Pincode
                                    </label>
                                    <div className="relative">
                                        <MapPinIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                        <input
                                            {...register('personalDetails.address.pincode')}
                                            disabled={!isEditing}
                                            className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl transition-all duration-200 focus:outline-none focus:ring-0 ${errors.personalDetails?.address?.pincode
                                                ? 'border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/20'
                                                : 'border-gray-200 dark:border-gray-600 focus:border-purple-500 dark:focus:border-purple-400'
                                                } ${!isEditing ? 'bg-gray-50 dark:bg-gray-700 cursor-not-allowed' : 'bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-500'} text-gray-900 dark:text-white`}
                                            placeholder="Enter pincode"
                                            maxLength={6}
                                        />
                                    </div>
                                    {errors.personalDetails?.address?.pincode && (
                                        <p className="text-sm text-red-600 dark:text-red-400 flex items-center">
                                            <span className="w-1 h-1 bg-red-500 rounded-full mr-2"></span>
                                            {errors.personalDetails.address.pincode.message}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Account Information */}
                    <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 dark:border-gray-700/50 p-8">
                        <div className="flex items-center mb-8">
                            <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-blue-500 rounded-xl flex items-center justify-center mr-4">
                                <BuildingOfficeIcon className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                    Account Information
                                </h2>
                                <p className="text-gray-600 dark:text-gray-400">
                                    Your account status and membership details
                                </p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="flex items-center space-x-4 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                                <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                                    <CalendarIcon className="h-6 w-6 text-white" />
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                        Member Since
                                    </p>
                                    <p className="text-lg font-bold text-blue-600 dark:text-blue-400">
                                        {profile ? formatDate(profile.createdAt) : 'N/A'}
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center space-x-4 p-6 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl border border-green-200 dark:border-green-800">
                                <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                                    <ShieldCheckIcon className="h-6 w-6 text-white" />
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                        Account Status
                                    </p>
                                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${profile?.isActive
                                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                        }`}>
                                        {profile?.isActive ? '✓ Active' : '✗ Inactive'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Profile Statistics */}
                    <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 dark:border-gray-700/50 p-8">
                        <div className="flex items-center mb-8">
                            <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mr-4">
                                <ChartBarIcon className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                    Profile Statistics
                                </h2>
                                <p className="text-gray-600 dark:text-gray-400">
                                    Your profile overview and activity
                                </p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {/* Skills Count */}
                            <div className="flex items-center space-x-4 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                                <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                                    <SparklesIcon className="h-6 w-6 text-white" />
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                        Skills
                                    </p>
                                    <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                                        {profile?.skills?.length || 0}
                                    </p>
                                </div>
                            </div>

                            {/* Experience Level */}
                            <div className="flex items-center space-x-4 p-6 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl border border-green-200 dark:border-green-800">
                                <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                                    <TrophyIcon className="h-6 w-6 text-white" />
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                        Experience
                                    </p>
                                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                                        {profile?.experience ? 'Expert' : 'Beginner'}
                                    </p>
                                </div>
                            </div>

                            {/* Profile Completeness */}
                            <div className="flex items-center space-x-4 p-6 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl border border-purple-200 dark:border-purple-800">
                                <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
                                    <ChartBarIcon className="h-6 w-6 text-white" />
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                        Completeness
                                    </p>
                                    <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                                        {calculateProfileCompleteness(profile)}%
                                    </p>
                                </div>
                            </div>

                            {/* Account Age */}
                            <div className="flex items-center space-x-4 p-6 bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 rounded-xl border border-orange-200 dark:border-orange-800">
                                <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center">
                                    <ClockIcon className="h-6 w-6 text-white" />
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                        Member Since
                                    </p>
                                    <p className="text-lg font-bold text-orange-600 dark:text-orange-400">
                                        {profile ? Math.floor((Date.now() - new Date(profile.createdAt).getTime()) / (1000 * 60 * 60 * 24)) : 0} days
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Quick Actions */}
                    {!isEditing && (
                        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 dark:border-gray-700/50 p-8">
                            <div className="flex items-center mb-8">
                                <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center mr-4">
                                    <PlusIcon className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                        Quick Actions
                                    </h2>
                                    <p className="text-gray-600 dark:text-gray-400">
                                        Common profile management tasks
                                    </p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Edit Profile */}
                                <button
                                    onClick={() => setIsEditing(true)}
                                    className="flex items-center space-x-4 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border border-blue-200 dark:border-blue-800 hover:from-blue-100 hover:to-indigo-100 dark:hover:from-blue-800/30 dark:hover:to-indigo-800/30 transition-all duration-200 group"
                                >
                                    <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                                        <PencilIcon className="h-6 w-6 text-white" />
                                    </div>
                                    <div className="text-left">
                                        <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                            Edit Profile
                                        </p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            Update your information
                                        </p>
                                    </div>
                                </button>

                                {/* Share Profile */}
                                <button
                                    onClick={async () => {
                                        try {
                                            if (navigator.share) {
                                                await navigator.share({
                                                    title: `${profile?.firstName} ${profile?.lastName} - Profile`,
                                                    text: `Check out ${profile?.firstName} ${profile?.lastName}'s profile`,
                                                    url: window.location.href
                                                });
                                            } else {
                                                await navigator.clipboard.writeText(window.location.href);
                                                dispatch(showToast({
                                                    message: 'Profile link copied to clipboard',
                                                    type: 'success'
                                                }));
                                            }
                                        } catch (error: any) {
                                            // Handle share cancellation or other errors silently
                                            if (error.name !== 'AbortError') {
                                                console.error('Share error:', error);
                                            }
                                        }
                                    }}
                                    className="flex items-center space-x-4 p-6 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl border border-purple-200 dark:border-purple-800 hover:from-purple-100 hover:to-pink-100 dark:hover:from-purple-800/30 dark:hover:to-pink-800/30 transition-all duration-200 group"
                                >
                                    <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center group-hover:bg-purple-600 transition-colors">
                                        <HeartIcon className="h-6 w-6 text-white" />
                                    </div>
                                    <div className="text-left">
                                        <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                            Share Profile
                                        </p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            Share with others
                                        </p>
                                    </div>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    {isEditing && (
                        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 dark:border-gray-700/50 p-8">
                            <div className="flex flex-col sm:flex-row items-center justify-end space-y-4 sm:space-y-0 sm:space-x-6">
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-3 border-2 border-gray-300 dark:border-gray-600 text-sm font-semibold rounded-xl text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200"
                                >
                                    <XMarkIcon className="h-5 w-5 mr-2" />
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-semibold rounded-xl hover:from-blue-700 hover:to-purple-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
                                >
                                    {saving ? (
                                        <>
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                            Saving Changes...
                                        </>
                                    ) : (
                                        <>
                                            <CheckIcon className="h-5 w-5 mr-2" />
                                            Save Changes
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
};

export default Profile;